import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useQueryKeys } from '@/shared/hooks/use-query-keys';
import {
  FetchCampaignPostCount,
  FetchCampaignPosts,
  type CampaignDetailPost,
  type CampaignPostListResponse,
  type FetchCampaignPostsRequest,
} from '..';

type QueryOptions = {
  enabled?: boolean;
  mode?: 'page' | 'accumulate';
  pageCount?: number;
};

export function useCampaignPosts(
  campaignId: string,
  recent: boolean,
  request?: FetchCampaignPostsRequest,
  options?: QueryOptions,
) {
  const queryKeys = useQueryKeys();
  const mode = options?.mode ?? 'page';
  const pageCount = mode === 'accumulate' ? Math.max(1, options?.pageCount ?? 1) : 1;
  const enabled = campaignId.length > 0 && (options?.enabled ?? true);
  const pageRequests = useMemo(
    () =>
      Array.from(
        { length: pageCount },
        (_, index): FetchCampaignPostsRequest => ({
          sort: request?.sort,
          limit: request?.limit,
          page: mode === 'accumulate' ? index + 1 : (request?.page ?? 1),
        }),
      ),
    [mode, pageCount, request],
  );

  const pageQueries = useQueries({
    queries: pageRequests.map((pageRequest) => ({
      enabled,
      queryKey: queryKeys.campaign.posts(campaignId, recent, pageRequest),
      queryFn: ({ signal }) => FetchCampaignPosts(campaignId, recent, pageRequest, signal),
      staleTime: 30_000,
    })),
  });

  const pages = pageQueries.flatMap((query) => (query.data ? [query.data] : []));
  const items = pages.flatMap((page) => page.items);
  const firstPage = pages[0];
  const data: CampaignPostListResponse | undefined =
    mode === 'page'
      ? firstPage
      : firstPage
        ? {
            items,
            pagination: firstPage.pagination,
          }
        : undefined;
  const totalCount = firstPage?.pagination.total ?? 0;
  const hasNextPage = mode === 'accumulate' && totalCount > items.length;
  const lastQuery = pageQueries[pageQueries.length - 1];
  const isFetchingNextPage =
    mode === 'accumulate' &&
    pageQueries.length > 1 &&
    (lastQuery?.isFetching ?? false) &&
    lastQuery.data == null;

  return {
    data,
    error: pageQueries.find((query) => query.error != null)?.error ?? null,
    hasNextPage,
    isError: pageQueries.some((query) => query.isError),
    isFetching: pageQueries.some((query) => query.isFetching),
    isFetchingNextPage,
    isLoading:
      mode === 'page'
        ? (pageQueries[0]?.isLoading ?? false)
        : (pageQueries[0]?.isLoading ?? false) && pages.length === 0,
    items: items as CampaignDetailPost[],
    pages,
    refetch: () => Promise.all(pageQueries.map((query) => query.refetch())),
    totalCount,
  };
}

export function useCampaignPostCounts(campaignId: string, options?: QueryOptions) {
  const queryKeys = useQueryKeys();
  const enabled = campaignId.length > 0 && (options?.enabled ?? true);
  const [totalQuery, recentQuery] = useQueries({
    queries: [
      {
        enabled,
        queryKey: queryKeys.campaign.postCount(campaignId, false),
        queryFn: ({ signal }) => FetchCampaignPostCount(campaignId, false, signal),
        staleTime: 30_000,
      },
      {
        enabled,
        queryKey: queryKeys.campaign.postCount(campaignId, true),
        queryFn: ({ signal }) => FetchCampaignPostCount(campaignId, true, signal),
        staleTime: 30_000,
      },
    ],
  });
  const total = totalQuery.data ?? 0;
  const recent = recentQuery.data ?? 0;
  const hasAnyData = totalQuery.data != null || recentQuery.data != null;

  return {
    data: hasAnyData ? { total, recent } : undefined,
    error: totalQuery.error ?? recentQuery.error ?? null,
    isError: totalQuery.isError || recentQuery.isError,
    isFetching: totalQuery.isFetching || recentQuery.isFetching,
    isLoading: totalQuery.isLoading || recentQuery.isLoading,
    recent,
    recentQuery,
    refetch: () => Promise.all([totalQuery.refetch(), recentQuery.refetch()]),
    total,
    totalQuery,
  };
}
